import type { SupabaseClient } from '@supabase/supabase-js'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = (supabase: SupabaseClient, t: string): any => (supabase as any).from(t)

export type CoaPaymentAccount = {
  id: string
  code: string | null
  name: string
  account_type: string | null
  is_payment_account: boolean
  is_receiving_account: boolean
  subgroup_id: string | null
  account_subgroups: { id: string; code: string; name: string } | null
}

// Legacy type — kept for POSPage backward compat
export interface PaymentAccount {
  id: string
  name: string
  type: 'cash' | 'bank' | 'pos_machine' | 'mobile_money' | 'other'
  bank_name?: string | null
  account_number?: string | null
  is_receiving: boolean
  balance?: number
  source: 'coa'
  coa_account_id: string | null
  current_balance?: number
  account_type?: string
}

export type SalesOrderPaymentMethod =
  | 'cash'
  | 'card_pos'
  | 'bank_transfer'
  | 'credit'
  | 'split'
  | null

export function normalisePaymentMethod(
  method: string | null | undefined,
): 'cash' | 'card_pos' | 'bank_transfer' {
  const m = (method ?? '').toLowerCase()
  if (m === 'pos' || m === 'card' || m === 'card_pos' || m === 'pos_machine') return 'card_pos'
  if (m === 'bank' || m === 'transfer' || m === 'bank_transfer' || m === 'mobile_money') return 'bank_transfer'
  return 'cash'
}

export function methodForAccountName(name: string): 'cash' | 'card_pos' | 'bank_transfer' {
  return normalisePaymentMethod(detectAccountType(name))
}

export function detectAccountType(
  name: string,
): PaymentAccount['type'] {
  const lower = name.toLowerCase()
  if (
    lower.includes('pos') ||
    lower.includes('terminal') ||
    lower.includes('moniepoint') ||
    lower.includes('opay pos')
  )
    return 'pos_machine'
  if (lower.includes('cash') || lower.includes('float') || lower.includes('petty'))
    return 'cash'
  if (lower.includes('mobile') || lower.includes('wallet') || lower.includes('opay wallet'))
    return 'mobile_money'
  if (
    lower.includes('bank') ||
    lower.includes('gtb') ||
    lower.includes('zenith') ||
    lower.includes('access') ||
    lower.includes('uba') ||
    lower.includes('first bank') ||
    lower.includes('fcmb') ||
    lower.includes('sterling') ||
    lower.includes('fidelity') ||
    lower.includes('lotus') ||
    lower.includes('kuda') ||
    lower.includes('standard') ||
    lower.includes('guaranty')
  )
    return 'bank'
  return 'other'
}

export async function getPaymentAccounts(
  supabase: SupabaseClient,
  tenantId: string,
): Promise<CoaPaymentAccount[]> {
  const { data, error } = await db(supabase, 'accounts')
    .select(`
      id, code, name, account_type,
      is_payment_account,
      is_receiving_account,
      subgroup_id,
      account_subgroups (
        id, code, name)
    `)
    .eq('tenant_id', tenantId)
    .eq('is_payment_account', true)
    .eq('is_active', true)
    .order('code')

  if (error) {
    console.error('getPaymentAccounts:', error)
    return []
  }

  return data ?? []
}

export async function getReceivingAccounts(
  supabase: SupabaseClient,
  tenantId: string,
): Promise<CoaPaymentAccount[]> {
  const { data } = await db(supabase, 'accounts')
    .select(`
      id, code, name,
      account_type,
      is_payment_account,
      is_receiving_account,
      subgroup_id,
      account_subgroups (id, code, name)
    `)
    .eq('tenant_id', tenantId)
    .eq('is_receiving_account', true)
    .eq('is_active', true)
    .order('code')

  return data ?? []
}

// Converts a CoaPaymentAccount to the legacy PaymentAccount shape for POSPage
export function toPaymentAccount(a: CoaPaymentAccount): PaymentAccount {
  return {
    id: a.id,
    name: a.name,
    type: detectAccountType(a.name),
    bank_name: null,
    account_number: null,
    is_receiving: a.is_receiving_account,
    source: 'coa',
    coa_account_id: a.id,
    current_balance: undefined,
    account_type: a.account_type ?? undefined,
  }
}
